<template>
  <AppWithLoading v-slot:default="{ loader }">
    <div class="interview-testing__test">
      <div v-for="(question, index) in sortedQuestions" :key="question.order">
        <TextQuestion
          v-if="isTextQuestion(question)"
          :question="question"
          :answers="answers"
          :index="index"
          :disabled-answers="disabledAnswers"
          @answer="answerQuestion"
          @setAnswer="setAnswer"
        />
        <SelectQuestion
          v-else-if="isSelectQuestion(question)"
          :question="question"
          :answers="answers"
          :index="index"
          :disabled-answers="disabledAnswers"
          @answer="answerQuestion"
          @setAnswer="setAnswer"
        />
        <NumericQuestion
          v-else-if="isNumericQuestion(question)"
          :question="question"
          :answers="answers"
          :index="index"
          :disabled-answers="disabledAnswers"
          @answer="answerQuestion"
          @setAnswer="setAnswer"
        />
      </div>
    </div>
    <div class="d-flex justify-center mt-5">
      <VBtn
        color="success"
        type="submit"
        elevation="0"
        :loading="questionSaving"
        :disabled="questionSaving"
        @click.prevent="loader(() => finishTest())"
      >
        {{ $t('finishTest') }}
      </VBtn>
    </div>
  </AppWithLoading>
</template>

<script>
import { toRefs, computed, ref } from '@vue/composition-api';
import { testQuestionTypes } from '@/enums';
import AppWithLoading from '../AppWithLoading.vue';
import TextQuestion from './TextQuestion.vue';
import NumericQuestion from './NumericQuestion.vue';
import SelectQuestion from './SelectQuestion.vue';

export default {
  name: 'TestForm',
  components: {
    AppWithLoading,
    TextQuestion,
    NumericQuestion,
    SelectQuestion,
  },
  props: {
    questions: {
      type: Array,
      required: true,
    },
    answers: {
      type: Array,
      required: true,
    },
    disabledAnswers: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { questions, disabledAnswers } = toRefs(props);

    const timer = ref(null);
    const questionImage = ref(null);

    const sortedQuestions = computed(() => {
      const newQuestion = [...questions.value];
      return newQuestion.sort((a, b) => a.order - b.order);
    });

    const questionsWithAnswers = ref(questions.value);

    const hasInvalidQuestions = computed(
      () => questionsWithAnswers.value.filter(q => !q.isValid).length > 0,
    );
    const isValid = computed(() => !(hasInvalidQuestions.value) || disabledAnswers.value);

    const saveTimeout = 400;

    const questionTimers = ref({});
    const loadingQuestions = ref([]);

    const questionSaving = computed(() => loadingQuestions.value.length > 0);

    function finishTest() {
      if (!isValid.value) {
        emit('openDialog');
        return;
      }

      if (!questionSaving.value && isValid.value) {
        emit('finishTest');
      }
    }

    async function answerQuestion(answer) {
      const { questionId } = answer;
      loadingQuestions.value.push(questionId);
      questionsWithAnswers.value = questionsWithAnswers.value.map((q) => {
        if (q.id === questionId) {
          q.isValid = answer.isValid;
        }
        return q;
      });

      if (questionTimers.value[questionId]) {
        clearTimeout(questionTimers.value[questionId]);
        delete questionTimers.value[questionId];
      }

      questionTimers.value[questionId] = setTimeout(async () => {
        await this.$store.dispatch('candidate/answerQuestion', {
          testQuestionId: questionId,
          answer: {
            answer: answer.answer,
          },
        });

        loadingQuestions.value = loadingQuestions.value.filter(qId => qId !== questionId);
        delete questionTimers.value[questionId];
      }, saveTimeout);
    }

    function isTextQuestion(q) {
      return [
        testQuestionTypes.TEXT,
        testQuestionTypes.FREE,
      ].includes(q?.type);
    }

    function isNumericQuestion(q) {
      return [
        testQuestionTypes.NUMERIC,
      ].includes(q?.type);
    }

    function isSelectQuestion(q) {
      return [
        testQuestionTypes.SELECT,
        testQuestionTypes.MULTISELECT,
      ].includes(q?.type);
    }

    function setAnswer(answer) {
      const { questionId } = answer;

      for (const question of questionsWithAnswers.value) {
        if (question.id === questionId) {
          question.isValid = answer.isValid;
          break;
        }
      }
    }

    return {
      testQuestionTypes,
      timer,
      questionImage,
      questionSaving,
      finishTest,
      sortedQuestions,
      answerQuestion,
      isTextQuestion,
      isNumericQuestion,
      isSelectQuestion,
      setAnswer,
    };
  },
};
</script>

<style lang="scss" scoped>
.interview-testing {
  &__test {
    margin-top: 8px;
  }
}
</style>

<i18n locale="en" lang="yaml">
  requiredField: This field is required
  finishTest: Finish the testing section
  enterTheAnswer: Enter your answer
</i18n>

<i18n locale="ru" lang="yaml">
  requiredField: Обязательное поле для заполнения
  finishTest: Закончить тестирование
  enterTheAnswer: Введите ответ
</i18n>
